/**
 * Landing v3 — the scroll-linked stretches always finish (user 2026-10-09):
 * when the reader stops scrolling half-way through one (two cards on screen
 * mid-throw, the chips half gathered, the deck half risen), the page carries
 * on by itself to the stretch's nearer end in the direction they were going —
 * so a resting page always shows ONE card, never a frame between two. It
 * takes over the moment the reader lets go (a trackpad's momentum starting to
 * slow, a mouse wheel going quiet), at the speed the page is moving, so the
 * finish is one motion — never a stop and a second start.
 *
 * The stretches are the timeline's own: hero → Ka's line (the gather), the
 * deck's rise → card 0 at rest, and each card at rest → the next. Between them
 * (Ka's play-once line, the last card's hold, the page below) scrolling is
 * free.
 */
import { type MotionValue, useMotionValueEvent } from 'framer-motion';
import { type RefObject, useEffect, useRef } from 'react';

import { DECK_RISE_SPAN, LANDING_TIMELINE } from './landing-features';

/** The stretches that snap, as [start, end] in viewport-heights. */
export const SNAP_STRETCHES: readonly (readonly [number, number])[] = (() => {
  const { kaStarts, cardThrowStart } = LANDING_TIMELINE;
  const first = cardThrowStart[0];
  return [
    [0, kaStarts[0]],
    [first - DECK_RISE_SPAN, first],
    ...cardThrowStart.slice(0, -1).map((at, g) => [at, cardThrowStart[g + 1]] as const),
  ];
})();

/** Past this share of a stretch in the direction of travel (a card is 1.6
 *  screens, so ~115px), it completes; short of it — a nudge, not a request
 *  to move on — it settles back. */
const COMMIT = 0.08;
/** A step that arrives with no momentum — an arrow key, a mouse wheel's
 *  notch, a slow drag — is a deliberate move, never a nudge: any real step
 *  into a stretch (~30px of a card) completes it. With the momentum
 *  threshold an arrow key's 40px was undone and the deck could not be left
 *  by keyboard. */
const STEP_COMMIT = 0.02;

/**
 * Where a page at `s` (viewport-heights) should settle, having last moved
 * down (`dir` 1) or up (-1); `null` when it is not inside a stretch. `aim` is
 * where its momentum was taking it (defaults to `s`): it decides how far
 * through the stretch the reader got, capped at the stretch's own ends — one
 * card per gesture, however hard the flick.
 */
export function deckSnapTarget(
  s: number,
  dir: 1 | -1,
  aim = s,
  commit = COMMIT,
): number | null {
  for (const [a, b] of SNAP_STRETCHES) {
    if (s <= a || s >= b) continue;
    const p = (Math.min(b, Math.max(a, aim)) - a) / (b - a);
    if (dir > 0) return p > commit ? b : a;
    return p < 1 - commit ? a : b;
  }
  return null;
}

/** A pause longer than this between wheel events starts a new gesture. */
const GESTURE_GAP_MS = 120;
/** The gesture must have reached this speed (px/ms, ~4px a frame) for its
 *  slowing to read as a release; slower ones settle on the idle timer. */
const MIN_PEAK_SPEED = 0.25;
/** Released once the smoothed speed has fallen to this share of the
 *  gesture's peak — the momentum's first frames, not its crawling tail. */
const RELEASE_SHARE = 0.85;
/** A mouse wheel's notch (and any line/page-mode delta): it never decays,
 *  so its stop is the idle timer's to read. */
const NOTCH_PX = 60;

/**
 * Reads a wheel stream for the moment the reader lets go. After the fingers
 * lift, a trackpad's momentum DECAYS, but in uneven steps with repeats
 * (18, 17, 17, 15, 14, 14…) — so the test is speed, not a strictly shrinking
 * series: once the smoothed speed has dropped well below the gesture's peak
 * and has stopped rising, the reader has released. Taking over THEN, at that
 * speed, is what makes the finish one motion; waiting for the stream to end
 * leaves a second of crawling tail that reads as a stop.
 */
export function createWheelReleaseDetector() {
  let peak = 0;
  let speed = 0;
  let lastT = 0;
  let lastAbs = 0;
  let sign = 0;
  let count = 0;
  let notRising = 0;
  const reset = () => {
    peak = 0;
    speed = 0;
    count = 0;
    notRising = 0;
  };
  return {
    reset,
    /** Feed one wheel event (`deltaPx` in pixels, `t` in ms); true when the
     *  gesture has just been released. */
    push(deltaPx: number, t: number): boolean {
      const abs = Math.abs(deltaPx);
      const dir = Math.sign(deltaPx);
      const dt = t - lastT;
      if (count === 0 || dt > GESTURE_GAP_MS || dir !== sign) reset();
      const v = abs / Math.max(count === 0 ? 16 : dt, 8);
      speed = count === 0 ? v : speed * 0.5 + v * 0.5;
      peak = Math.max(peak, speed);
      notRising = count > 0 && abs <= lastAbs ? notRising + 1 : 0;
      lastT = t;
      lastAbs = abs;
      sign = dir;
      count++;
      if (abs >= NOTCH_PX) return false;
      return (
        count >= 4 &&
        peak >= MIN_PEAK_SPEED &&
        speed <= peak * RELEASE_SHARE &&
        notRising >= 2
      );
    },
  };
}

/** How long a finish of `distPx` takes: longer for a longer way, but never a
 *  crawl (~0.65s for a whole card, ~0.26s for a short settle). */
export function finishDuration(distPx: number): number {
  return Math.min(700, Math.max(260, 120 + Math.sqrt(Math.abs(distPx)) * 14));
}

/**
 * Where the finish is at `u` (0→1 of its duration `ms`): a cubic that LEAVES at
 * the reader's own speed (`v0`, px/ms) and LANDS at rest on the card — the
 * speed is continuous at the hand-over, so there is no lurch from the finger's
 * pace to the finish's, and no overshoot (the start slope is capped where the
 * cubic would run past the card).
 */
export function finishCurve(u: number, from: number, to: number, v0: number, ms: number) {
  const d = to - from;
  if (d === 0) return to;
  const slope = Math.min(2.5, Math.max(0, (v0 * ms) / d));
  const u2 = u * u;
  const u3 = u2 * u;
  return (
    from * (2 * u3 - 3 * u2 + 1) + d * slope * (u3 - 2 * u2 + u) + to * (3 * u2 - 2 * u3)
  );
}

/** How far ahead the released momentum would have carried the page, in
 *  seconds of its current speed — where the finish is decided from, so a
 *  flick that would have crossed the line completes and a soft one settles
 *  back without first running on. */
const MOMENTUM_LOOKAHEAD_S = 0.3;

/** No wheel event for this long: a mouse wheel's notches have stopped (they
 *  never decay), or a trackpad stopped dead. */
const WHEEL_IDLE_MS = 70;
/** Any other input (keys, a touch, the scrollbar): the scroll must be this
 *  still before the page finishes the stretch. */
const SETTLE_MS = 140;

export function useDeckSnap(
  scrollY: MotionValue<number>,
  stageSizeRef: RefObject<{ w: number; h: number }>,
  disabled: boolean,
) {
  const timer = useRef<number | undefined>(undefined);
  const wheelIdle = useRef<number | undefined>(undefined);
  const last = useRef(0);
  const dir = useRef<1 | -1>(1);
  // A held pointer (dragging the scrollbar) is the reader still steering.
  const held = useRef(false);
  const finishing = useRef<{ stop: () => void } | null>(null);
  const detector = useRef(createWheelReleaseDetector());
  // The released momentum's tail, swallowed until it runs out — through the
  // finish AND after it lands, or it would carry the page on past the card.
  const tail = useRef({ active: false, abs: 0, t: 0, sign: 0 });

  const vh = () => stageSizeRef.current?.h || window.innerHeight || 1;

  const stopFinishing = () => {
    finishing.current?.stop();
    finishing.current = null;
  };

  /** Carry the page from where it is, at the speed it is going, to the end
   *  of the stretch it is in. False when it is not inside one. */
  /** `momentum`: a released trackpad gesture (its speed decides how far it
   *  got); otherwise a step with no momentum, which always completes. */
  const finish = (lead = 0, momentum = false): boolean => {
    if (held.current) return false;
    const h = vh();
    // `lead`: the delta of the wheel event that triggered the take-over —
    // applied now, so the hand-over frame moves like the ones around it.
    const from = scrollY.get() + lead;
    const velocity = scrollY.getVelocity();
    const target = deckSnapTarget(
      from / h,
      dir.current,
      (from + velocity * MOMENTUM_LOOKAHEAD_S) / h,
      momentum ? COMMIT : STEP_COMMIT,
    );
    if (target === null) return false;
    stopFinishing();
    const to = Math.round(target * h);
    // Settling back starts from rest: never running on past the line first.
    const v0 = Math.sign(velocity) === Math.sign(to - from) ? velocity / 1000 : 0;
    const ms = finishDuration(to - from);
    if (lead) window.scrollTo({ top: from, behavior: 'instant' });
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const u = Math.min(1, (now - start) / ms);
      window.scrollTo({ top: finishCurve(u, from, to, v0, ms), behavior: 'instant' });
      if (u < 1) frame = requestAnimationFrame(step);
      else finishing.current = null;
    };
    frame = requestAnimationFrame(step);
    finishing.current = { stop: () => cancelAnimationFrame(frame) };
    return true;
  };

  useMotionValueEvent(scrollY, 'change', (y) => {
    if (y !== last.current) dir.current = y > last.current ? 1 : -1;
    last.current = y;
    window.clearTimeout(timer.current);
    // Under reduced motion nothing is thrown — no frame between two cards.
    if (disabled || finishing.current) return;
    timer.current = window.setTimeout(() => finish(), SETTLE_MS);
  });

  useEffect(() => {
    if (disabled) return;
    const onWheel = (e: WheelEvent) => {
      // A pinch (ctrl) or ⌘-zoom belongs to the canvas under the cursor.
      if (e.ctrlKey || e.metaKey || e.deltaY === 0) return;
      // A dialog's scroll lock: the page is not the reader's to move.
      if (document.body.hasAttribute('data-scroll-locked')) return;
      // Line / page deltas (Firefox's mouse wheel) in pixels, roughly.
      const dy =
        e.deltaMode === 0 ? e.deltaY : e.deltaY * (e.deltaMode === 1 ? 16 : vh());
      const abs = Math.abs(dy);
      const now = e.timeStamp;
      const t = tail.current;
      if (t.active) {
        // The rest of the released momentum — same way, no stronger, still
        // streaming: the finish carries it (or has already landed), so it is
        // swallowed instead of crawling the page on past the card.
        if (
          Math.sign(dy) === t.sign &&
          abs <= Math.max(t.abs * 1.3, t.abs + 3) &&
          now - t.t < GESTURE_GAP_MS
        ) {
          tail.current = { active: true, abs, t: now, sign: t.sign };
          e.preventDefault();
          return;
        }
        tail.current = { ...t, active: false };
        detector.current.reset();
      }
      // Any wheel the finish is not swallowing is the reader steering again
      // (a new push, the other way, or a finish the idle timer started):
      // never animate against their own scroll.
      stopFinishing();
      window.clearTimeout(wheelIdle.current);
      if (detector.current.push(dy, now) && finish(dy, true)) {
        tail.current = { active: true, abs, t: now, sign: Math.sign(dy) };
        e.preventDefault();
        return;
      }
      wheelIdle.current = window.setTimeout(() => {
        detector.current.reset();
        finish();
      }, WHEEL_IDLE_MS);
    };
    // The scrollbar is a mouse's; a finger's hold is the touch events' below.
    const down = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      held.current = true;
      tail.current = { ...tail.current, active: false };
      stopFinishing();
      window.clearTimeout(timer.current);
    };
    const up = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      held.current = false;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => finish(), SETTLE_MS);
    };
    // A key takes the page back too.
    const steer = () => {
      tail.current = { ...tail.current, active: false };
      stopFinishing();
    };
    // A finger on a touch screen steers until it lifts (a pan cancels its
    // pointer at once, so `pointerup` would end the hold under the finger).
    const touchStart = () => {
      held.current = true;
      steer();
      window.clearTimeout(timer.current);
    };
    const touchEnd = (e: TouchEvent) => {
      if (e.touches.length) return;
      held.current = false;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => finish(), SETTLE_MS);
    };
    // Capture: a canvas that stops the wheel on its way down (the mind map
    // keeps plain wheels from its zoom) must not hide it from the finish.
    window.addEventListener('wheel', onWheel, { capture: true, passive: false });
    window.addEventListener('pointerdown', down);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    window.addEventListener('keydown', steer);
    window.addEventListener('touchstart', touchStart, { passive: true });
    window.addEventListener('touchend', touchEnd, { passive: true });
    window.addEventListener('touchcancel', touchEnd, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel, { capture: true });
      window.removeEventListener('pointerdown', down);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      window.removeEventListener('keydown', steer);
      window.removeEventListener('touchstart', touchStart);
      window.removeEventListener('touchend', touchEnd);
      window.removeEventListener('touchcancel', touchEnd);
      window.clearTimeout(timer.current);
      window.clearTimeout(wheelIdle.current);
      stopFinishing();
    };
    // `finish` reads only refs and the scroll value.
  }, [disabled]);
}
